import type { WavetablePreset, WavetableRuntimeTable, WtModRoute, WtModTarget } from "../types";
import type { VoiceNoteFxState } from "../kernel/noteFxVoice";
import { createNoteFxState } from "../kernel/noteFxVoice";
import { sampleWavetable } from "./sample";
import { layoutUnisonVoices } from "./unison";
import { WAVETABLE_MAX_UNISON } from "./types";

export type WtUnisonPhase = {
  phase1: number;
  phase2: number;
};

export type WtVoiceState = {
  active: boolean;
  note: number;
  velocity: number;
  velocitySmooth: number;
  panSmooth: number;
  pan: number | null;
  freqHz: number;
  targetFreqHz: number;
  ampEnv: { stage: string; level: number; stageTime: number; releaseFrom: number };
  modEnv: { stage: string; level: number; stageTime: number; releaseFrom: number };
  lfoPhase: number;
  filterZ1: number;
  filterZ2: number;
  noiseState: number;
  subPhase: number;
  unison: WtUnisonPhase[];
  noteFx: VoiceNoteFxState;
};

export function createWtVoiceState(): WtVoiceState {
  return {
    active: false,
    note: 60,
    velocity: 0,
    velocitySmooth: 0,
    panSmooth: 0,
    pan: null,
    freqHz: 440,
    targetFreqHz: 440,
    ampEnv: { stage: "idle", level: 0, stageTime: 0, releaseFrom: 0 },
    modEnv: { stage: "idle", level: 0, stageTime: 0, releaseFrom: 0 },
    lfoPhase: 0,
    filterZ1: 0,
    filterZ2: 0,
    noiseState: 1,
    subPhase: 0,
    unison: Array.from({ length: WAVETABLE_MAX_UNISON }, () => ({ phase1: 0, phase2: 0 })),
    noteFx: createNoteFxState(),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wtModAmount(
  routes: WtModRoute[],
  source: WtModRoute["source"],
  target: WtModTarget,
  value: number,
  polarity: "unipolar" | "bipolar" = "unipolar",
): number {
  let sum = 0;
  for (const route of routes) {
    if (route.source !== source || route.target !== target) {
      continue;
    }
    const v = polarity === "bipolar" ? value : value;
    sum += (route.invert ? -route.depth : route.depth) * v;
  }
  return sum;
}

function equalPower(pan: number): { left: number; right: number } {
  const t = clamp(pan, -1, 1);
  const angle = (t + 1) * (Math.PI / 4);
  return { left: Math.cos(angle), right: Math.sin(angle) };
}

function noiseSample(state: { value: number }): number {
  state.value ^= state.value << 13;
  state.value ^= state.value >> 17;
  state.value ^= state.value << 5;
  return (state.value | 0) / 2147483648;
}

function applySvFilter(
  input: number,
  type: WavetablePreset["filter"]["type"],
  cutoffHz: number,
  resonance: number,
  sampleRate: number,
  z1: { v: number },
  z2: { v: number },
): number {
  // Chamberlin SVF blows up when f approaches/exceeds ~1 (fc near sr/6).
  // Match the subtractive synth path: cap fc, clamp f/q, and bound state.
  const fc = clamp(cutoffHz, 20, sampleRate * 0.2);
  const f = Math.min(0.95, 2 * Math.sin(Math.PI * (fc / sampleRate)));
  const q = clamp(1 - resonance * 0.9, 0.1, 1);
  const low = z2.v + f * z1.v;
  const high = input - low - q * z1.v;
  const band = z1.v + f * high;
  if (!Number.isFinite(low) || !Number.isFinite(band) || !Number.isFinite(high)) {
    z1.v = 0;
    z2.v = 0;
    return 0;
  }
  z1.v = clamp(band, -8, 8);
  z2.v = clamp(low, -8, 8);
  if (type === "highpass") {
    return clamp(high, -8, 8);
  }
  if (type === "bandpass") {
    return z1.v;
  }
  return z2.v;
}

export type WtRenderDeps = {
  sampleRate: number;
  dt: number;
  preset: WavetablePreset;
  voice: WtVoiceState;
  getTable: (id: string) => WavetableRuntimeTable | undefined;
  amp: number;
  mod: number;
  pitchModSemi: number;
  cutoffOctExtra: number;
};

/**
 * Render one sample of stereo wavetable voice (unison pans baked in).
 * Envelopes must already be advanced by the caller (`amp` / `mod` levels).
 */
export function renderWavetableSample(deps: WtRenderDeps): { l: number; r: number } {
  const { sampleRate, dt, preset, voice, getTable, amp, mod, pitchModSemi, cutoffOctExtra } = deps;
  const key = (voice.note - 60) / 12;

  voice.lfoPhase += preset.lfo.rateHz * dt;
  const lfoWave = preset.lfo.wave === "tri"
    ? 1 - 4 * Math.abs((voice.lfoPhase % 1) - 0.5)
    : Math.sin(2 * Math.PI * (voice.lfoPhase % 1));
  const lfo = lfoWave * preset.lfo.depth;
  const lfoU = (lfo + 1) * 0.5;

  const routes = preset.routes;
  const pitchMod =
    wtModAmount(routes, "modEnv", "pitch", mod)
    + wtModAmount(routes, "ampEnv", "pitch", amp)
    + wtModAmount(routes, "lfo", "pitch", lfoU)
    + wtModAmount(routes, "key", "pitch", key, "bipolar")
    + pitchModSemi;
  const cutoffMod =
    wtModAmount(routes, "modEnv", "cutoff", mod)
    + wtModAmount(routes, "ampEnv", "cutoff", amp)
    + wtModAmount(routes, "lfo", "cutoff", lfoU)
    + wtModAmount(routes, "key", "cutoff", key, "bipolar")
    + cutoffOctExtra;
  const ampMod =
    wtModAmount(routes, "modEnv", "amp", mod)
    + wtModAmount(routes, "lfo", "amp", lfoU)
    + wtModAmount(routes, "key", "amp", key, "bipolar");
  const pos1Mod =
    wtModAmount(routes, "modEnv", "tablePos1", mod)
    + wtModAmount(routes, "lfo", "tablePos1", lfoU)
    + wtModAmount(routes, "key", "tablePos1", key, "bipolar");
  const pos2Mod =
    wtModAmount(routes, "modEnv", "tablePos2", mod)
    + wtModAmount(routes, "lfo", "tablePos2", lfoU)
    + wtModAmount(routes, "key", "tablePos2", key, "bipolar");
  const warp1Mod =
    wtModAmount(routes, "modEnv", "warp1", mod)
    + wtModAmount(routes, "lfo", "warp1", lfoU);
  const warp2Mod =
    wtModAmount(routes, "modEnv", "warp2", mod)
    + wtModAmount(routes, "lfo", "warp2", lfoU);
  const detuneMod =
    wtModAmount(routes, "modEnv", "unisonDetune", mod)
    + wtModAmount(routes, "lfo", "unisonDetune", lfoU);
  const spreadMod =
    wtModAmount(routes, "modEnv", "unisonSpread", mod)
    + wtModAmount(routes, "lfo", "unisonSpread", lfoU);
  const decayMod =
    wtModAmount(routes, "modEnv", "unisonEdgeDecay", mod)
    + wtModAmount(routes, "lfo", "unisonEdgeDecay", lfoU);
  const pmMod =
    wtModAmount(routes, "modEnv", "pmDepth", mod)
    + wtModAmount(routes, "lfo", "pmDepth", lfoU);
  const subMod =
    wtModAmount(routes, "modEnv", "subLevel", mod)
    + wtModAmount(routes, "lfo", "subLevel", lfoU);

  const layout = layoutUnisonVoices(
    preset.voices,
    Math.max(0, preset.unisonDetune + detuneMod * 50),
    clamp(preset.unisonSpread + spreadMod, 0, 1),
    clamp(preset.unisonEdgeDecay + decayMod, 0, 1),
  );

  const table1 = getTable(preset.osc1.tableId);
  const table2 = getTable(preset.osc2.tableId);
  const pos1 = clamp(preset.osc1.position + pos1Mod, 0, 1);
  const pos2 = clamp(preset.osc2.position + pos2Mod, 0, 1);
  const warp1 = clamp(preset.osc1.warp + warp1Mod, 0, 1);
  const warp2 = clamp(preset.osc2.warp + warp2Mod, 0, 1);
  const pmDepth = Math.max(0, preset.pmDepth + pmMod);

  let sumL = 0;
  let sumR = 0;
  let levelSum = 0;

  for (let i = 0; i < layout.length; i++) {
    const u = layout[i]!;
    const phases = voice.unison[i]!;
    const baseHz = voice.freqHz * 2 ** ((pitchMod + u.detuneCents / 100) / 12);
    const hz1 = baseHz * 2 ** (preset.osc1.octave + preset.osc1.semis / 12 + preset.osc1.detuneCents / 1200);
    const hz2 = baseHz * 2 ** (preset.osc2.octave + preset.osc2.semis / 12 + preset.osc2.detuneCents / 1200);

    const prev1 = phases.phase1;
    phases.phase1 += hz1 / sampleRate;
    let s1 = 0;
    if (table1 && preset.osc1.level > 0) {
      s1 = sampleWavetable(table1, phases.phase1, pos1, preset.osc1.warpMode, warp1) * preset.osc1.level;
    }
    // Hard sync: reset osc2 when osc1 wraps
    if (preset.sync && Math.floor(phases.phase1) > Math.floor(prev1)) {
      phases.phase2 = 0;
    }
    phases.phase2 += hz2 / sampleRate;
    let s2 = 0;
    if (table2 && preset.osc2.level > 0) {
      const pmPhase = phases.phase2 + s1 * pmDepth;
      s2 = sampleWavetable(table2, pmPhase, pos2, preset.osc2.warpMode, warp2) * preset.osc2.level;
    }
    const mixed = (s1 + s2) * u.level;
    const { left, right } = equalPower(u.pan);
    sumL += mixed * left;
    sumR += mixed * right;
    levelSum += u.level;
  }

  const norm = levelSum > 1e-6 ? 1 / Math.sqrt(levelSum) : 1;
  sumL *= norm * 0.45;
  sumR *= norm * 0.45;

  // Shared sub + noise (mono into both)
  const subLevel = clamp(preset.sub.level + subMod, 0, 1);
  if (subLevel > 0) {
    const subHz = voice.freqHz * 2 ** (preset.sub.octave + pitchMod / 12);
    voice.subPhase += subHz / sampleRate;
    const sp = ((voice.subPhase % 1) + 1) % 1;
    const sub = preset.sub.wave === "square"
      ? (sp < 0.5 ? 1 : -1) * 0.7
      : Math.sin(2 * Math.PI * sp);
    const s = sub * subLevel * 0.5;
    sumL += s;
    sumR += s;
  }
  if (preset.noise.level > 0) {
    const ns = { value: voice.noiseState | 0 || 1 };
    const n = noiseSample(ns) * preset.noise.level * 0.35;
    voice.noiseState = ns.value;
    sumL += n;
    sumR += n;
  }

  const cutoff = clamp(preset.filter.cutoffHz * 2 ** cutoffMod, 40, 18000);
  const z1 = { v: voice.filterZ1 };
  const z2 = { v: voice.filterZ2 };
  // Filter mid, then re-apply stereo ratio (cheap linked dual-mono)
  const mid = 0.5 * (sumL + sumR);
  const side = 0.5 * (sumL - sumR);
  const filtered = applySvFilter(mid, preset.filter.type, cutoff, preset.filter.resonance, sampleRate, z1, z2);
  voice.filterZ1 = z1.v;
  voice.filterZ2 = z2.v;
  sumL = filtered + side;
  sumR = filtered - side;

  voice.velocitySmooth += (voice.velocity - voice.velocitySmooth) * Math.min(1, dt * 80);
  const gain = amp * voice.velocitySmooth * (1 + ampMod);
  return {
    l: Number.isFinite(sumL * gain) ? sumL * gain : 0,
    r: Number.isFinite(sumR * gain) ? sumR * gain : 0,
  };
}
